import { useEffect, useState } from 'react';
import { useEffectiveChildMode } from '@/components/utils/useEffectiveChildMode';
import { getStoredPersona } from '@/utils/persona';
import { adultProfile, childProfile, supervisorProfile, teenProfile } from './registry';
import type { Persona, PersonaProfile } from './types';
import type { AppRole } from './useRole';
import useRole from './useRole';

// useEffectiveChildMode exporterar inte sin Options-typ; vi speglar den EXAKT
// här (korrektion 2) så att reaktiviteten bevaras när options skickas vidare.
// Skiljer signaturen sig i useEffectiveChildMode måste den uppdateras här med.
type Options = {
  currentClient?: unknown;
  isSupervising?: boolean;
};

/**
 * Speglar useEffectiveChildMode:s källval för supervising/klient EXAKT (samma
 * options-företräde, samma sessionStorage/localStorage-fallback). Used both för
 * att härleda R1-young-signalen och hålla den byte-identiskt reaktiv. Ändras
 * useEffectiveChildMode måste denna uppdateras med.
 *
 *   - Med Options (context i scope): currentClient != null ELLER isSupervising.
 *   - Utan Options (App.tsx): sessionStorage 'currentClient' != null ELLER
 *     localStorage 'supervising' med length>0.
 */
export const readSupervisingOrHasClient = (options?: Options): boolean => {
  const hasClient =
    options !== undefined
      ? options.currentClient != null
      : sessionStorage.getItem('currentClient') != null;

  const fallbackSupervising = localStorage.getItem('supervising');
  const isSupervising =
    options?.isSupervising !== undefined
      ? options.isSupervising
      : !!fallbackSupervising && fallbackSupervising.length > 0;

  return hasClient || isSupervising;
};

/**
 * Ren prioritetsregel (korrektion R1 — barn-/young-läckage):
 *   1. Barnläge-gaten vinner: effectiveChildMode → teen (persona='teen') annars
 *      barn. Garanterar att barnvyerna bara nås i barnläge.
 *   2. Icke-barnläge + rollvalet 'supervisor' → handledarprofil (egen navigation
 *      och identitet). Delningen av handledarläget och brukarläget.
 *   3. Icke-barnläge + handledare/vald klient → vuxenprofil ALLTID. En
 *      handledare får ALDRIG young-profilen oavsett localStorage.persona.
 *   4. Annars → vuxenprofil.
 *
 * `role` är en OPTIONELL fjärde parameter, medvetet sist och medvetet optionell:
 * de tre första argumenten och deras semantik är oförändrade, så alla befintliga
 * anrop och tester med tre argument ger byte-identiskt samma profil som förut
 * (role === undefined → aldrig supervisorProfile).
 *
 * Rollen är ETT LAGER OVANPÅ, inte en andra sanning: den avgör bara vilken
 * PRESENTATION handledaren får. Gaterna (SuperviseGuard) och effectiveChildMode
 * läser fortfarande ENBART isSupervising/currentClient ur SupervisorContext, och
 * ordning 1 ovan betyder att barnläge-gaten fortsatt slår rollen — en roll i
 * localStorage kan aldrig lyfta ett konto ur barnvyn.
 *
 * Exporteras ren och testbar (inga hooks, inga sidoeffekter).
 */
export const resolveActiveProfile = (
  effectiveChildMode: boolean,
  persona: Persona | null,
  supervisingOrHasClient: boolean,
  role?: AppRole | null,
): PersonaProfile => {
  if (effectiveChildMode) {
    if (persona === 'teen') return teenProfile;
    return childProfile;
  }
  if (role === 'supervisor') return supervisorProfile;
  if (supervisingOrHasClient) return adultProfile;
  return adultProfile;
};

/**
 * Aktiv PersonaProfile för det inloggade brukar-läget. Bygger ovanpå
 * useEffectiveChildMode (ENDA gate-sanningen) och persona ur localStorage,
 * lyssnar reaktivt på 'storage' (setPersona dispatchar det i samma flik).
 *
 * Options speglar useEffectiveChildMode:s signatur och skickas vidare oförändrat
 * (korrektion 2) — utelämnas i App.tsx, skickas där SupervisorContext finns.
 */
export const useActivePersona = (options?: Options): PersonaProfile => {
  const effectiveChildMode = useEffectiveChildMode(options);
  const [persona, setPersonaState] = useState<Persona | null>(getStoredPersona());
  const [supervising, setSupervising] = useState<boolean>(() =>
    readSupervisingOrHasClient(options),
  );
  // Rollen läses ur RoleContext, inte ur localStorage: rollnyckeln är
  // app:role:<user.tag> och tag:en finns bara i AppContext. Konsumenter som ligger
  // UTANFÖR RoleProvider (App.tsx:s tema, som mountas ovanför SupervisorScene) får
  // RoleContext:s default role=null och därmed exakt samma profil som förut.
  const { role } = useRole();

  useEffect(() => {
    const handleStorageChange = () => {
      setPersonaState(getStoredPersona());
      setSupervising(readSupervisingOrHasClient(options));
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return resolveActiveProfile(effectiveChildMode, persona, supervising, role);
};
